import {
  DOCUMENT_FILE_EXTENSIONS,
  DOCUMENT_MIME_TYPES,
  type DocumentFileExtension,
  type DocumentMimeType,
  isSupportedDocumentFile,
} from "@/constants/document-formats";
import type { ApiResponse } from "../common-types";

export const ALLOWED_DOCUMENT_EXTENSIONS = DOCUMENT_FILE_EXTENSIONS;
export const ALLOWED_DOCUMENT_MIME_TYPES = DOCUMENT_MIME_TYPES;

export type AllowedDocumentExtension = DocumentFileExtension;
export type AllowedDocumentMimeType = DocumentMimeType;

export class InvalidFileTypeError extends Error {
  fileName: string;
  allowedExtensions: readonly string[];

  constructor(fileName: string, allowedExtensions: readonly string[]) {
    super(
      `El archivo "${fileName}" no es un tipo permitido. Tipos permitidos: ${allowedExtensions.join(", ")}`
    );
    this.name = "InvalidFileTypeError";
    this.fileName = fileName;
    this.allowedExtensions = allowedExtensions;
  }
}

export function validateFileType(file: File | Blob): void {
  let fileName = "unknown";

  if ("name" in file && typeof file.name === "string") {
    fileName = file.name;
  }

  if (!isSupportedDocumentFile(file)) {
    throw new InvalidFileTypeError(fileName, ALLOWED_DOCUMENT_EXTENSIONS);
  }
}

export type DocumentsRepositoryMethods = {
  getDocumentsList: () => Promise<ApiResponse<GetDocumentsListResponse>>;
  getRequestsCount: () => Promise<ApiResponse<GetRequestsCountResponse>>;
  getRequestsDetail: () => Promise<ApiResponse<GetRequestsDetailResponse>>;
  getRequestDocuments: (processId: string) => Promise<ApiResponse<GetRequestDocumentsResponse>>;
  deleteDocument: (documentId: string) => Promise<ApiResponse<DeleteDocumentResponse>>;
  uploadDocument: (payload: UploadDocumentPayload) => Promise<ApiResponse<UploadDocumentResponse>>;
  updateDocument: (
    documentId: string,
    payload: UpdateDocumentPayload
  ) => Promise<ApiResponse<UpdateDocumentResponse>>;
};

// Existing types
export interface GetDocumentsListResponse {
  documents: Document[];
}

export interface Document {
  documentId: string;
  title: string;
  format: string;
  uploadedAt: number | string;
  signedUrl: string;
}

// 1. Get requests count
export interface GetRequestsCountResponse {
  count: number;
}

// 2. Get requests detail
export type GetRequestsDetailResponse = ProcessRequest[];

export interface ProcessRequest {
  processId: string;
  processName: string;
  companyName: string;
  deadlineDate: string; // ISO 8601 datetime
  status: string;
  companyLogoUrl: string;
  stageId?: string;
}

// 3. Get request documents by process
export type GetRequestDocumentsResponse = RequestDocument[];

export interface RequestDocument {
  processId: string;
  name: string;
  status: "pending" | "uploaded";
  submittedAt: string | null; // ISO 8601 datetime
  requiredDocumentId: string;
  uploadedDocumentId: string | null;
  fileName: string | null;
  stageId?: string;
  templateUrl?: string | null;
}

// 5. Delete document
export interface DeleteDocumentResponse {
  success: boolean;
}

// 6. Upload document
export type DocumentMetadata = Record<string, string>;

export interface UploadDocumentPayload {
  file?: File | Blob;
  documentId?: string; // preloaded document ID
  fileName?: string; // fileName for preloaded documents
  documentType?: string;
  metadata?: DocumentMetadata;
  processId?: string;
  requiredDocumentId?: string; // for document requests
  stageId?: string;
}

export interface UploadDocumentError {
  index: number;
  error: string;
}

export interface UploadDocumentResult {
  success: boolean;
  documentId: string;
  fileName: string;
  fileSize: number;
  uploadedAt: number;
  signedUrl: string;
}

export interface UploadDocumentResponse {
  success: boolean;
  results: UploadDocumentResult[];
  errors?: UploadDocumentError[];
}

// 7. Update document
export interface UpdateDocumentPayload {
  file: File | Blob | string; // file or base64 string
}

export interface UpdateDocumentResponse {
  documentId: string;
  updated: boolean;
}
